import { SITE } from "./seo";

const u = (path: string) => new URL(path, SITE.url).toString();

const PUBLISHER = {
  "@type": "Organization",
  name: "Chikitsa",
  url: SITE.url,
  logo: { "@type": "ImageObject", url: u("/assets/logo-chikitsa.svg") },
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE.url}#organization`,
    name: "Chikitsa",
    legalName: SITE.legalName,
    brand: "Chikitsa",
    url: SITE.url,
    logo: u("/assets/logo-chikitsa.svg"),
    image: u(SITE.ogImage),
    description: SITE.defaultDescription,
    email: SITE.contact.email,
    telephone: SITE.contact.phone,
    /** Map all corporate and registered offices to Google's knowledge graph */
    location: SITE.contact.locations.map(loc => ({
      "@type": ["LocalBusiness", "MedicalOrganization"],
      name: loc.name,
      image: u(SITE.ogImage),
      telephone: SITE.contact.phone,
      priceRange: "$$",
      openingHours: "Mo-Fr 09:00-18:00",
      address: {
        "@type": "PostalAddress",
        streetAddress: loc.streetAddress,
        addressLocality: loc.addressLocality,
        addressRegion: loc.addressRegion,
        postalCode: loc.postalCode,
        addressCountry: loc.addressCountry,
      }
    })),
    sameAs: [
      "https://vigorus.ai",
      SITE.socials.linkedin,
    ],
    parentOrganization: {
      "@type": "Organization",
      name: "Vigorus AI",
      url: "https://vigorus.ai",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: SITE.contact.phone,
        contactType: "customer support",
        email: SITE.contact.email,
        areaServed: "IN",
        availableLanguage: ["en", "hi"],
      },
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.url}#website`,
    name: "Chikitsa",
    url: SITE.url,
    inLanguage: "en-IN",
    publisher: { "@id": `${SITE.url}#organization` },
    /**
     * SearchAction — enables Google Sitelinks Searchbox for branded queries.
     * Points to the contact/demo page with the search query pre-filled.
     */
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE.url}/contact?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * WebPage + SoftwareApplication schema for product/platform pages.
 * Implements the structure recommended in the SEO audit.
 */
export function webPageSchema(input: {
  path: string;
  name: string;
  description: string;
}) {
  const canonical = new URL(input.path, SITE.url).toString();
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: input.name,
    description: input.description,
    isPartOf: { "@id": `${SITE.url}#website` },
    publisher: { "@id": `${SITE.url}#organization` },
    breadcrumb: { "@id": `${canonical}#breadcrumb` },
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "Chikitsa",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Hospital Information Management System",
      operatingSystem: "Web",
      countriesSupported: "IN",
      description: input.description,
      publisher: { "@id": `${SITE.url}#organization` },
    },
  };
}


export function softwareAppSchema(overrides?: {
  name?: string;
  description?: string;
  path?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: overrides?.name ?? "Chikitsa HIMS",
    applicationCategory: "MedicalBusinessApplication",
    operatingSystem: "Web",
    description: overrides?.description ?? SITE.defaultDescription,
    url: u(overrides?.path ?? "/"),
    image: u(SITE.ogImage),
    softwareVersion: "2026",
    brand: { "@type": "Brand", name: "Chikitsa" },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: u("/contact"),
    },
    featureList: [
      "ABDM-ready (ABHA, HIE-CM, FHIR R4)",
      "NHCX claim flows & PMJAY pre-auth",
      "AI documentation (Voice-MD, Lipi, Sahyogi)",
      "Audit-ready Personal Health Records (PHR)",
      "OPD, IPD, EMR, Pharmacy, Lab, Billing modules",
      "ISO/IEC 27001 certified",
    ],
    publisher: { "@id": `${SITE.url}#organization` },
  };
}

export function productSchema(input: {
  name: string;
  description: string;
  path: string;
  category?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: input.name,
    description: input.description,
    brand: { "@type": "Brand", name: "Chikitsa" },
    category: input.category ?? "Hospital Software",
    url: u(input.path),
    image: u(SITE.ogImage),
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: u("/contact"),
    },
  };
}

export type FaqItem = { q: string; a: string };

export function faqSchema(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  const canonical = items.length > 0 ? u(items[items.length - 1].path) : SITE.url;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${canonical}#breadcrumb`,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: u(it.path),
    })),
  };
}

export function blogSchema(
  posts: Array<{
    slug: string;
    title: string;
    excerpt: string;
    author: string;
    datePublished: string;
  }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The Chikitsa Journal",
    url: u("/blog"),
    publisher: PUBLISHER,
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      author: { "@type": "Person", name: p.author },
      datePublished: p.datePublished,
      mainEntityOfPage: u(`/blog#${p.slug}`),
      image: u(SITE.ogImage),
      publisher: PUBLISHER,
    })),
  };
}
